import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Row, Col } from 'reactstrap';
import ReactECharts from 'echarts-for-react';
import axios from 'axios';
import LogoSpinner from '../../Components/Common/LogoSpinner';

const SubscriptionDistribution = ({ dateRange }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [metadata, setMetadata] = useState(null);

    // Helper function to check if data is sample/placeholder data
    const isSampleData = () => {
        // Check if data is missing or empty
        if (!data || !metadata || data.length === 0) {
            return true;
        }
        
        // Check if total subscriptions and revenue are zero
        if ((metadata.total_subscriptions === 0 || !metadata.total_subscriptions) &&
            (metadata.total_revenue === 0 || !metadata.total_revenue)) {
            return true;  
        }
        
        // Check if all plan values are 0
        const allValuesZero = data.every(item => item.value === 0);
        
        return allValuesZero;
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const authToken = JSON.parse(sessionStorage.getItem('authUser'))?.token;

            if (!authToken) {
                throw new Error('Authentication token not found');
            }

            const response = await axios.get(`${process.env.REACT_APP_API_URL1}analytics/dashboard/subscription-distribution`, {
                params: {
                    start_date: dateRange?.start?.toISOString(),
                    end_date: dateRange?.end?.toISOString()
                },
                headers: {
                    'Authorization': `${authToken}`
                }
            });

            if (response.data) {
                setData(response.data.distribution);
                setMetadata(response.data.metadata);
            }
        } catch (error) {
            console.error('Error fetching subscription data:', error);
            setError('Failed to load subscription data');
        } finally {
            setLoading(false);
        }
    };

    // Listen for WebSocket updates
    useEffect(() => {
        const handleSubscriptionDistributionUpdate = (updateData) => {
            console.log('SubscriptionDistribution component received update:', updateData);
            if (updateData) {
                if (updateData.distribution) {
                    setData(updateData.distribution);
                }
                if (updateData.metadata) {
                    setMetadata(updateData.metadata);
                }
                setLoading(false);
            }
        };
        
        // Add event listener
        if (window.dashboardEvents) {
            console.log('SubscriptionDistribution - Setting up event listener for dashboard:subscriptionDistribution');
            window.dashboardEvents.on('dashboard:subscriptionDistribution', handleSubscriptionDistributionUpdate);
        } else {
            console.warn('SubscriptionDistribution - window.dashboardEvents is not available');
        }
        
        // Cleanup function
        return () => {
            if (window.dashboardEvents) {
                window.dashboardEvents.off('dashboard:subscriptionDistribution', handleSubscriptionDistributionUpdate);
            }
        };
    }, []);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 300000); // Update every 5 minutes
        return () => clearInterval(interval);
    }, [dateRange]);

    const option = {
        tooltip: {
            trigger: 'item',
            formatter: (params) => {
                const details = data?.find(item => item.name === params.name)?.details;
                if (details) {
                    return `
                        <strong>${params.name} Plan</strong><br/>
                        Subscribers: ${params.value.toLocaleString()}<br/>
                        Monthly Revenue: $${details.monthly_revenue.toLocaleString()}<br/>
                        Avg Duration: ${details.average_duration} months<br/>
                        Churn Rate: ${details.churn_rate}%<br/>
                        Growth Rate: ${details.growth_rate}%<br/>
                        New (30d): ${details.new_subscribers_30d}
                    `;
                }
                return `${params.name}: ${params.value} (${params.percent}%)`;
            }
        },
        legend: {
            orient: 'horizontal',
            bottom: 5,
            left: 'center',
            icon: 'circle',
            textStyle: {
                fontSize: 12
            },
            itemGap: 20,
            padding: [0, 0, 0, 0]
        },
        series: [
            {
                name: 'Subscription Plans',
                type: 'pie',
                radius: ['35%', '65%'],  // Smaller radius to leave room for labels
                center: ['50%', '45%'],  // Center the chart a bit higher
                avoidLabelOverlap: true,
                itemStyle: {
                    borderRadius: 5,
                    borderColor: '#fff',
                    borderWidth: 1
                },
                label: {
                    show: true,
                    position: 'outside',
                    formatter: '{b}\n{d}%',  // Put percentage on new line
                    fontSize: 12,
                    lineHeight: 15,
                    rich: {
                        b: {
                            fontSize: 12,
                            lineHeight: 15,
                            fontWeight: 'bold'
                        }
                    }
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 14,
                        fontWeight: 'bold'
                    }
                },
                labelLine: {
                    show: true,
                    length: 10,
                    length2: 5,
                    smooth: true
                },
                data: data?.map(item => ({
                    value: item.value,
                    name: item.name,
                    itemStyle: {
                        color: item.name === 'Essential' ? '#6366f1' : 
                               item.name === 'Premium' ? '#10b981' : 
                               item.name === 'Black' ? '#f59e0b' : '#64748b'
                    }
                })) || []
            }
        ]
    };

    return (
        <Card style={{ height: "100%" }}>
            <CardHeader>
                <h4 className="card-title mb-0">Subscription Distribution</h4>
            </CardHeader>
            <CardBody style={{ height: "630px" }}>
                {loading || isSampleData() ? (
                    <div className="text-center py-5">
                        <LogoSpinner message="Loading subscription data..." size="large" />
                    </div>
                ) : error ? (
                    <div className="text-center py-5 text-danger">
                        <i className="ri-error-warning-line fs-3 mb-3 d-block"></i>
                        {error}
                    </div>
                ) : (
                    <>
                        <Row className="text-center mb-2">
                            <Col xs={6}>
                                <h2 className="mb-0 fs-3 fw-bold">{metadata?.total_subscriptions.toLocaleString()}</h2>
                                <p className="text-muted mb-0 small">Total Subscriptions</p>
                            </Col>
                            <Col xs={6}>
                                <h2 className="mb-0 fs-3 fw-bold">${metadata?.total_revenue.toLocaleString()}</h2>
                                <p className="text-muted mb-0 small">Total Revenue</p>
                            </Col>
                        </Row>
                        <ReactECharts 
                            option={option} 
                            style={{ height: "90%", marginTop: "-10px" }}
                            opts={{ renderer: 'svg' }}
                            notMerge={true}
                            lazyUpdate={true}
                        />
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default SubscriptionDistribution; 